import { Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { StateService, UIRouterGlobals } from "@uirouter/core";
import { debounce } from "lodash";
import { forkJoin } from "rxjs";
import { finalize, tap } from "rxjs/operators";
import { environment as appEnv } from "../../../environments/environment";
import { NotifierPropertiesService } from "../../models/notifier-properties.service";
import { QueriesService } from "../../models/queries.service";
import { OutdatedNotifierService } from "../../outdated-notifier/outdated-notifier.service";
import { RoundTimeService } from "../../round-time/round-time.service";

@Component({
  selector: "ab-query-list",
  templateUrl: "./query-list.component.html",
})
export class QueryListComponent implements OnChanges {
  @Input() public project: any;
  @Input() public timeDurAgo: string;
  @Input() public lastN: number;
  @Input() public dur: string;
  @Input() public queryPage: number;
  @Input() public sortColumn: string;
  @Input() public sortDesc: boolean;
  @Input() public search: string;
  @Input() public environment: string;

  public loading: boolean;
  public queries: any;
  public appEnv = appEnv;
  public totalPages: number;
  public totalQueries: number;
  public debounceSearchQuery = debounce(() => {
    this.searchQuery();
  }, 1500);
  public needsNotifierUpgrade: boolean;
  public notifierName: string;

  private PAGE_SIZE = 20;

  constructor(
    private queriesService: QueriesService,
    private roundTimeService: RoundTimeService,
    private $state: StateService,
    private $uiRouterGlobals: UIRouterGlobals,
    private outdatedNotifierService: OutdatedNotifierService,
    private notifierPropertiesService: NotifierPropertiesService,
  ) {}

  public ngOnChanges(changes: SimpleChanges) {
    if (this.queryPage == null) {
      return;
    }
    if (changes.timeDurAgo && !changes.timeDurAgo.isFirstChange()) {
      this.$state.go(
        this.$uiRouterGlobals.current.name,
        { queryPage: 1 },
        { location: "replace" },
      );
    }

    const queriesObservable = this.setQueries();

    this.needsNotifierUpgrade = false;
    const params = { projectId: this.project.id };
    const notifierPropsObservable = this.notifierPropertiesService
      .get(params)
      .pipe(
        tap((data: any) => {
          const name = data.notifier_name;
          const version = data.notifier_version;

          this.notifierName = data.notifier_name;
          if (this.outdatedNotifierService.isOutdatedForAPM(name, version)) {
            this.needsNotifierUpgrade = true;
          }
        }),
      );

    forkJoin([queriesObservable, notifierPropsObservable]).subscribe();
  }

  public trackBy(_i: number, query: any) {
    return query.queryId;
  }

  public sortBy(column: string) {
    let sortDesc;
    if (column === this.sortColumn) {
      sortDesc = !this.sortDesc;
    } else {
      // Default sort for query which sorts by (file, line, query) is ascending
      // and for others descending.
      sortDesc = column !== "query";
    }

    this.$state.go(this.$uiRouterGlobals.current.name, {
      sortQueryColumn: column,
      sortQueryDesc: sortDesc,
      queryPage: 1,
    });
  }

  public shouldShowCTA(): boolean {
    if (
      this.needsNotifierUpgrade &&
      this.queries &&
      this.queries.length === 0
    ) {
      return true;
    } else {
      return false;
    }
  }

  private setQueries() {
    this.loading = true;

    const params = {
      projectId: this.project.id,
      time__gte: this.roundTimeService.timestampFor(this.timeDurAgo, this.dur),
      order: this.sortColumn,
      page: this.queryPage,
      limit: this.PAGE_SIZE,
    } as any;
    if (this.sortDesc) {
      params.order += " desc";
    }
    if (this.search) {
      params.query__ieq = this.search;
    }
    if (this.environment) {
      params.environment = this.environment;
    }

    return this.queriesService.query(params).pipe(
      tap((data: any) => {
        this.queries = data.queries;
        this.totalQueries = data.count;
        this.totalPages = Math.ceil(this.totalQueries / this.PAGE_SIZE);
      }),
      finalize(() => {
        this.loading = false;
      }),
    );
  }

  private searchQuery() {
    this.$state.go(this.$uiRouterGlobals.current.name, {
      queryPage: 1,
      querySearch: this.search,
    });
  }
}
